import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Trophy, BookOpen, DollarSign, Award } from 'lucide-react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { memo } from 'react';
import type { Course } from '../../types/course';

interface CourseListProps {
  courses: Course[];
  isLoading: boolean;
}

export const CourseList = memo(function CourseList({ courses, isLoading }: CourseListProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No courses available at the moment</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {courses.map((course) => (
        <a
          key={course.id}
          href={course.affiliate_url || '#'}
          target={course.affiliate_url ? "_blank" : undefined}
          rel={course.affiliate_url ? "noopener noreferrer" : undefined}
          className="bg-background-card rounded-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer border border-gray-800"
        >
          <div className="relative h-48">
            <img
              src={course.image_url}
              alt={course.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>

          <div className="p-4">
            <h3 className="text-lg font-semibold text-white mb-2">{course.title}</h3>
            <p className="text-gray-400 text-sm line-clamp-2 mb-4">
              {course.description}
            </p>

            <div className="space-y-2">
              <div className="flex items-center text-sm text-gray-400">
                <BookOpen className="h-4 w-4 mr-2" />
                {course.provider}
              </div>
              <div className="flex items-center text-sm text-gray-400">
                <Clock className="h-4 w-4 mr-2" />
                {course.duration}
              </div>
              <div className="flex items-center text-sm text-gray-400">
                <Trophy className="h-4 w-4 mr-2" />
                {course.level}
              </div>
              {course.salary_range && (
                <div className="flex items-center text-sm text-gray-400">
                  <DollarSign className="h-4 w-4 mr-2" />
                  {course.salary_range}
                </div>
              )}
              {course.has_certification && (
                <div className="flex items-center text-sm text-emerald-400">
                  <Award className="h-4 w-4 mr-2" />
                  Certification Available
                </div>
              )}
            </div>

            {course.course_marketplace_listings?.[0] && (
              <div className="mt-4 flex items-center justify-between">
                <div>
                  {course.course_marketplace_listings[0].sale_price ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-bold text-accent-green">
                        ${(course.course_marketplace_listings[0].sale_price / 100).toFixed(2)}
                      </span>
                      <span className="text-sm text-gray-400 line-through">
                        ${(course.course_marketplace_listings[0].price / 100).toFixed(2)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-lg font-bold text-accent-green">
                      {course.course_marketplace_listings[0].price === 0 
                        ? 'Free' 
                        : `$${(course.course_marketplace_listings[0].price / 100).toFixed(2)}`}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </a>
      ))}
    </div>
  );
});